import { ColorTypes, dates } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { CellModel, ColumnSource, RequestFilters, RowSource } from "@omegagrid/grid";
import { ToolbarItem } from "@omegagrid/toolbar";
import rp, { GridEditorAdapter, GridEditorOptions, RowData, createSchedulePeriodText, createScheduleText } from "@reporting/rp-app-host";
import { Task } from "@reporting/rp-types/deployment";

const PAGE_SIZE = 500;

/**
 * Scheduled tasks of the application servers (DPL_TASK in the deployment db, SA only). Read-only
 * like the report e-mails: the schedule is shown as text (the rule, the validity period and the
 * next run) and every field is edited in the task form sidebar; the grid itself only deletes (the
 * row action marks the row, Save posts it).
 */
export class TaskGridEditorAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;
		// the user's date format, the one the schedule texts use too: read here, once the locale is
		// set (the editor's default is resolved at module load and follows the browser)
		const dateTimeFormat = dates.getDateFormat({ withTime: true });
		this._columns = [{
			data: 'controls',
			t: 'a',
			e: false,
			w: 5 + 20 * 4,
			content: [{
				action: 'delete',
				title: msg('Delete'),
				icon: 'trash',
				color: ColorTypes.Red,
			}, {
				action: 'duplicateTask',
				title: msg('Duplicate'),
				icon: 'copy',
				color: ColorTypes.Yellow,
			}, {
				action: 'editTask',
				title: msg('Edit'),
				icon: 'pen',
				color: ColorTypes.Gray,
			}, {
				action: 'runTask',
				title: msg('Run now'),
				icon: 'play',
				color: ColorTypes.Green,
			}],
			filter: false,
		}, {
			data: 'id_task',
			header: '#',
			t: 'n',
			w: 60,
		}, {
			data: 'name',
			header: msg('Name'),
			t: 't',
			w: 220,
		}, {
			data: 'enabled',
			header: msg('Enabled'),
			t: 'b',
			e: false,
			w: 90,
		}, {
			data: 'script',
			header: msg('Script'),
			t: 't',
			w: 320,
		}, {
			data: 'script_type',
			header: msg('Script type'),
			t: 't',
			w: 110,
		}, {
			data: 'target_server',
			header: msg('Target server'),
			t: 't',
			w: 160,
		}, {
			data: 'schedule',
			header: msg('Schedule'),
			t: 't',
			w: 400,
			filter: false,
		}, {
			data: 'period',
			header: msg('Validity'),
			t: 't',
			w: 170,
			filter: false,
		}, {
			data: 'next_run',
			header: msg('Next run'),
			t: 'dt',
			z: dateTimeFormat,
			w: 145,
		}, {
			data: 'last_run',
			header: msg('Last run'),
			t: 'dt',
			z: dateTimeFormat,
			w: 145,
			hidden: true,
		}, {
			data: 'idz',
			t: 'n',
			hidden: true,
		}];
		return this._columns;
	}

	async getRows(filters?: RequestFilters): Promise<RowSource[]> {
		const tasks = await rp.ms('deployment').get<Task[]>('tasks', this.createRequestParams(filters, {
			order: ['name'],
			count: PAGE_SIZE,
		}));
		return tasks.map(task => ({
			data: {
				...task,
				schedule: createScheduleText(task),
				period: createSchedulePeriodText(task),
				// a schedule that is over has no next run: the stop icon instead of an empty cell
				next_run: task.next_run ?? { t: 'h', v: '<og-icon icon="circle-stop"></og-icon>' },
			},
		}));
	}

	async save(rowData: RowData[]) {
		for (const row of rowData) {
			if (row.state === 'deleted' && row.data.id_task) {
				await rp.ms('deployment').delete(`tasks/${row.data.id_task}`);
			}
		}
	}

	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			editable: false,
			pageSize: PAGE_SIZE,
			refreshAfterSave: true,
			filters: true,
			colLetters: false,
			rowNumbers: false,
			columnSorting: true,
			lightTheme: null,
			darkTheme: null,
			toolbar: {
				new: {enabled: false},
			},
			// the built-in "new" inserts an editable row; a task is created in the form instead
			customToolbar: [{
				type: 'button',
				id: 'addTask',
				icon: 'plus',
				color: ColorTypes.Blue,
				text: msg('Add task'),
			}] as ToolbarItem[],
			rowMenu: {
				deleteRows: true,
			},
		});
	}

	async onAction(action: string, cell: CellModel): Promise<void> {
		const task = cell?.row?.data as Task;
		switch (action) {
			case 'addTask':
				rp.host.openModalSidebar({
					id: 'deployment-taskForm',
					caption: msg('New task'),
				});
				break;
			case 'editTask':
				rp.host.openModalSidebar({
					id: `deployment-taskForm-${task.id_task}`,
					caption: msg('Edit task'),
				});
				break;
			case 'duplicateTask':
				rp.host.openModalSidebar({
					id: `deployment-taskForm-${task.id_task}-copy`,
					caption: msg('Duplicate task'),
				});
				break;
			case 'runTask': {
				const command = await rp.host.openMenu({
					content: 'yes_no',
					text: `${msg('Run the task now?')} ${task.name}`,
				});
				if (command?.id != 'Y') return;
				try {
					await rp.ms('deployment').post(`tasks/${task.id_task}/run`, {});
					rp.host.bucket.showAlert({ type: 'success', content: msg('The task will run at the next poll of its server.') });
				} catch (error) {
					rp.host.bucket.showAlert({ type: 'danger', title: msg('Failed to run the task'), content: String(error) });
				}
				this.editor.refresh();
				break;
			}
		}
	}

}
